class ProductMediaCarousel extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.carousel = null
    this.modalCarousel = null
    this.header = null
  }

  setupListeners () {
    this.initialiseCarousel()
    this.setupZoom()
    this.setupStickyMedia()
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.carousel?.destroy()
    this.modalCarousel?.destroy()
  }

  /*
  |--------------------------------------------------------------------------
  | Sticky Media
  |--------------------------------------------------------------------------
  */

  setupStickyMedia () {
    this.header = document.querySelector('.section-header')

    this.onStickyResize = this.onStickyResize.bind(this)

    this.listeners.add(
      window,
      'resize',
      this.onStickyResize,
      { passive: true },
    )

    this.listeners.add(
      window,
      'load',
      this.onStickyResize,
    )

    this.listeners.add(
      window,
      'pageshow',
      this.onStickyResize,
    )

    window.requestAnimationFrame(() => {
      this.updateStickyTop()
    })
  }

  onStickyResize () {
    this.updateStickyTop()
  }

  getHeaderBottom () {
    if (!this.header) return 0

    return Math.max(
      0,
      Math.round(
        this.header.getBoundingClientRect().bottom,
      ),
    )
  }

  updateStickyTop () {
    const stickyTop = this.getHeaderBottom()

    this.style.setProperty(
      '--ProductMedia-StickyTop',
      `${stickyTop}px`,
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Main Carousel
  |--------------------------------------------------------------------------
  */

  initialiseCarousel () {
    const carouselElement = this.querySelector(
      '[data-product-media-carousel]',
    )

    if (!carouselElement) return

    const imagesPerView = Number(
      this.dataset.imagesPerView || 2,
    )

    const mobileImagesPerView = Number(
      this.dataset.mobileImagesPerView || 1,
    )

    const imageGap = Number(
      this.dataset.imageGap || 0,
    )

    const dots = [
      ...this.querySelectorAll('[data-product-media-dot]'),
    ]

    const progressContainer = this.querySelector(
      '[data-product-media-progress]',
    )

    const progressBar = this.querySelector(
      '[data-product-media-progress-bar]',
    )

    const plugins = [
      window.BAO.Carousel.plugins.setInitialised(),
    ]

    /*
    |--------------------------------------------------------------------------
    | Dots
    |--------------------------------------------------------------------------
    */

    if (dots.length) {
      plugins.push(
        window.BAO.Carousel.plugins.useDots(dots),
      )
    }

    /*
    |--------------------------------------------------------------------------
    | Progress
    |--------------------------------------------------------------------------
    */

    if (progressContainer && progressBar) {
      plugins.push((carousel) => {
        const updateProgress = () => {
          const details = carousel.track.details

          if (!details) return

          const maximumIndex = details.maxIdx
          const currentIndex = details.rel

          progressContainer.setAttribute(
            'aria-hidden',
            String(maximumIndex === 0),
          )

          const progress =
            maximumIndex === 0
              ? 100
              : ((currentIndex + 1) /
                  (maximumIndex + 1)) *
                100

          progressBar.style.setProperty(
            '--Bar_Width',
            `${progress}%`,
          )
        }

        carousel.on(
          'created',
          updateProgress,
        )

        carousel.on(
          'slideChanged',
          updateProgress,
        )

        carousel.on(
          'updated',
          updateProgress,
        )
      })
    }

    /*
    |--------------------------------------------------------------------------
    | Create Main Carousel
    |--------------------------------------------------------------------------
    */

    this.carousel = new window.KeenSlider(
      carouselElement,
      {
        loop: false,

        slides: {
          perView: mobileImagesPerView,
          spacing: imageGap,
        },

        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: imagesPerView,
              spacing: imageGap,
            },
          },
        },
      },

      plugins,
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Zoom
  |--------------------------------------------------------------------------
  */

  setupZoom () {
    const zoomButton = this.querySelector(
      '[data-product-media-zoom]',
    )

    const modal = this.querySelector(
      '[data-product-media-modal]',
    )

    const closeButton = this.querySelector(
      '[data-product-media-modal-close]',
    )

    if (!modal) return

    /*
    |--------------------------------------------------------------------------
    | Open
    |--------------------------------------------------------------------------
    */

    if (zoomButton) {
      this.listeners.add(
        zoomButton,
        'click',
        () => {
          this.openZoom()
        },
      )
    }

    /*
    |--------------------------------------------------------------------------
    | Close
    |--------------------------------------------------------------------------
    */

    if (closeButton) {
      this.listeners.add(
        closeButton,
        'click',
        () => {
          this.closeZoom()
        },
      )
    }

    /*
    |--------------------------------------------------------------------------
    | Click Outside
    |--------------------------------------------------------------------------
    */

    this.listeners.add(
      modal,
      'click',
      (event) => {
        if (event.target === modal) {
          this.closeZoom()
        }
      },
    )

    /*
    |--------------------------------------------------------------------------
    | Escape
    |--------------------------------------------------------------------------
    */

    this.listeners.add(
      modal,
      'cancel',
      (event) => {
        event.preventDefault()

        this.closeZoom()
      },
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Open Zoom
  |--------------------------------------------------------------------------
  */

  openZoom () {
    if (!this.carousel) return

    const modal = this.querySelector(
      '[data-product-media-modal]',
    )

    const modalCarouselElement =
      this.querySelector(
        '[data-product-media-modal-carousel]',
      )

    if (
      !modal ||
      !modalCarouselElement
    ) {
      return
    }

    /*
    |--------------------------------------------------------------------------
    | Get Current Main Position
    |--------------------------------------------------------------------------
    |
    | This is the position the modal MUST open at.
    |
    */

    const mainDetails =
      this.carousel.track.details

    if (!mainDetails) return

    const currentIndex =
      mainDetails.rel

    /*
    |--------------------------------------------------------------------------
    | Show Modal
    |--------------------------------------------------------------------------
    */

    modal.showModal()

    /*
    |--------------------------------------------------------------------------
    | Initialise / Update
    |--------------------------------------------------------------------------
    */

    requestAnimationFrame(() => {
      /*
      |--------------------------------------------------------------------------
      | First Open
      |--------------------------------------------------------------------------
      */

      if (!this.modalCarousel) {
        this.initialiseModalCarousel(
          currentIndex,
        )
      }

      /*
      |--------------------------------------------------------------------------
      | Load Media And Then Sync
      |--------------------------------------------------------------------------
      |
      | Keen update() happens inside loadModalMedia().
      | The move happens AFTER that update so Keen cannot restore the
      | previous modal position afterwards.
      |
      */

      this.loadModalMedia(
        currentIndex,
      )
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Close Zoom
  |--------------------------------------------------------------------------
  */

  closeZoom () {
    const modal = this.querySelector(
      '[data-product-media-modal]',
    )

    if (!modal) return

    /*
    |--------------------------------------------------------------------------
    | Sync Main Carousel
    |--------------------------------------------------------------------------
    |
    | Do this while the modal is still visible so the user never sees the
    | main carousel slide into position.
    |
    */

    this.syncMainCarousel()

    /*
    |--------------------------------------------------------------------------
    | Close
    |--------------------------------------------------------------------------
    */

    modal.close()
  }

  /*
  |--------------------------------------------------------------------------
  | Modal Carousel
  |--------------------------------------------------------------------------
  */

  initialiseModalCarousel (initialIndex = 0) {
    const carouselElement =
      this.querySelector(
        '[data-product-media-modal-carousel]',
      )

    const previousButton =
      this.querySelector(
        '[data-product-media-modal-previous]',
      )

    const nextButton =
      this.querySelector(
        '[data-product-media-modal-next]',
      )

    if (!carouselElement) return

    const imagesPerView = Number(
      this.dataset.imagesPerView || 2,
    )

    const imageGap = Number(
      this.dataset.imageGap || 0,
    )

    /*
    |--------------------------------------------------------------------------
    | Update Arrows
    |--------------------------------------------------------------------------
    */

    const updateArrows = (carousel) => {
      const details =
        carousel.track.details

      if (!details) return

      const currentIndex =
        details.rel

      const maximumIndex =
        details.maxIdx

      if (previousButton) {
        previousButton.disabled =
          currentIndex <= 0
      }

      if (nextButton) {
        nextButton.disabled =
          currentIndex >= maximumIndex
      }
    }

    /*
    |--------------------------------------------------------------------------
    | Plugins
    |--------------------------------------------------------------------------
    */

    const plugins = [
      (carousel) => {
        carousel.on(
          'created',
          () => {
            updateArrows(carousel)
          },
        )

        carousel.on(
          'slideChanged',
          () => {
            updateArrows(carousel)
          },
        )

        carousel.on(
          'updated',
          () => {
            updateArrows(carousel)
          },
        )
      },
    ]

    /*
    |--------------------------------------------------------------------------
    | Create Modal Carousel
    |--------------------------------------------------------------------------
    */

    this.modalCarousel =
      new window.KeenSlider(
        carouselElement,
        {
          initial: initialIndex,

          loop: false,

          slides: {
            perView: imagesPerView,
            spacing: imageGap,
          },
        },

        plugins,
      )

    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */

    if (previousButton) {
      this.listeners.add(
        previousButton,
        'click',
        () => {
          this.modalCarousel?.prev()
        },
      )
    }

    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */

    if (nextButton) {
      this.listeners.add(
        nextButton,
        'click',
        () => {
          this.modalCarousel?.next()
        },
      )
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Load Modal Media
  |--------------------------------------------------------------------------
  */

  loadModalMedia (targetIndex = null) {
    const modalCarouselElement =
      this.querySelector(
        '[data-product-media-modal-carousel]',
      )

    if (!modalCarouselElement) return

    const images = [
      ...modalCarouselElement.querySelectorAll(
        'img',
      ),
    ]

    const videos = [
      ...modalCarouselElement.querySelectorAll(
        'video',
      ),
    ]

    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    modalCarouselElement.setAttribute(
      'data-loading',
      'true',
    )

    /*
    |--------------------------------------------------------------------------
    | Image Promises
    |--------------------------------------------------------------------------
    */

    const imagePromises =
      images.map((image) => {
        if (image.complete) {
          return Promise.resolve()
        }

        return new Promise(
          (resolve) => {
            image.addEventListener(
              'load',
              resolve,
              {
                once: true,
              },
            )

            image.addEventListener(
              'error',
              resolve,
              {
                once: true,
              },
            )
          },
        )
      })

    /*
    |--------------------------------------------------------------------------
    | Video Promises
    |--------------------------------------------------------------------------
    */

    const videoPromises =
      videos.map((video) => {
        /*
        |--------------------------------------------------------------------------
        | Already Loaded
        |--------------------------------------------------------------------------
        |
        | HAVE_CURRENT_DATA = 2.
        | At this point the first frame is available.
        |
        */

        if (video.readyState >= 2) {
          return Promise.resolve()
        }

        /*
        |--------------------------------------------------------------------------
        | Start Loading
        |--------------------------------------------------------------------------
        */

        video.load()

        return new Promise(
          (resolve) => {
            video.addEventListener(
              'loadeddata',
              resolve,
              {
                once: true,
              },
            )

            video.addEventListener(
              'error',
              resolve,
              {
                once: true,
              },
            )
          },
        )
      })

    /*
    |--------------------------------------------------------------------------
    | Media Loaded
    |--------------------------------------------------------------------------
    */

    Promise.all([
      ...imagePromises,
      ...videoPromises,
    ]).then(() => {
      if (!this.modalCarousel) return

      /*
      |--------------------------------------------------------------------------
      | Update Keen
      |--------------------------------------------------------------------------
      |
      | This must happen BEFORE moving to the required slide.
      |
      */

      this.modalCarousel.update()

      /*
      |--------------------------------------------------------------------------
      | Move To Main Carousel Position
      |--------------------------------------------------------------------------
      */

      if (targetIndex !== null) {
        const details =
          this.modalCarousel.track.details

        if (details) {
          const maximumIndex =
            details.maxIdx

          const index =
            Math.min(
              targetIndex,
              maximumIndex,
            )

          this.modalCarousel.moveToIdx(
            index,
            false,
            {
              duration: 0,
            },
          )
        }
      }

      /*
      |--------------------------------------------------------------------------
      | Reveal Carousel
      |--------------------------------------------------------------------------
      */

      modalCarouselElement.setAttribute(
        'data-loading',
        'false',
      )
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Sync Main Carousel
  |--------------------------------------------------------------------------
  */

  syncMainCarousel () {
    if (
      !this.carousel ||
      !this.modalCarousel
    ) {
      return
    }

    const modalDetails =
      this.modalCarousel.track.details

    const mainDetails =
      this.carousel.track.details

    if (
      !modalDetails ||
      !mainDetails
    ) {
      return
    }

    /*
    |--------------------------------------------------------------------------
    | Modal Position
    |--------------------------------------------------------------------------
    */

    const modalIndex =
      modalDetails.rel

    const maximumIndex =
      mainDetails.maxIdx

    const targetIndex =
      Math.min(
        modalIndex,
        maximumIndex,
      )

    this.carousel.moveToIdx(
      targetIndex,
      false,
      {
        duration: 0,
      },
    )
  }
}

customElements.define(
  'product-media-carousel',
  ProductMediaCarousel,
)
